// eslint-disable-next-line import/no-cycle
import NetworkAuth from '@/helpers/networking/NetworkAuth';
import axios from 'axios';
import i18n from '@/includes/i18n';
import keycloak from '../../../keycloak';
// eslint-disable-next-line import/no-cycle
import FilesService from '@/services/filesService';

export default {
  state: {
    token: localStorage.getItem('jwt') || null,
    refresh_token: localStorage.getItem('jwt_refresh_token') || null,
    user: {
      roles: [],
    },
    /** Merged sidebar from login */
    customerNavigation: JSON.parse(localStorage.getItem('customerNavigation') || '[]'),
    /** True after login or ping applied layout (even when nav array is empty). */
    customerNavigationLoaded: localStorage.getItem('customerNavigationLoaded') === 'true',
    /** Home section order from login */
    homeScreenLayout: JSON.parse(localStorage.getItem('homeScreenLayout') || '[]'),
    /** catalogKey → { en, de } from Features Manager (includes overlay features e.g. developmentTip) */
    featureDisplayByKey: JSON.parse(localStorage.getItem('featureDisplayByKey') || '{}'),
    /** catalogKey → { visible, locked, description? } for overlays and gated teasers */
    featureAccessByKey: JSON.parse(localStorage.getItem('featureAccessByKey') || '{}'),
    customerLayoutRevision: Number(localStorage.getItem('customerLayoutRevision') || '0') || 0,
    registerData: null,
    registerDataValid: false,
    passwordResetRequested: false,
    registrationSuccessful: false,
    newPasswordSetSuccesful: false,
    lang: localStorage.getItem('lang') || 'en',
    // New structure: tagGroups contains all tag groups with embedded tagOptions
    // Each group has: text {en, de}, protected (boolean), tagOptions [{_id, text, identifier}]
    tagGroups: [],
    selectedTags: [], // Selected tag option IDs (for unprotected/checkbox groups)
    tagGroupAnswers: {}, // Answers for protected/radio groups: { groupId: selectedTagOptionId }
  },
  getters: {
    isLoggedIn: (state) => !!state.token,
    getUser: (state) => state.user,
    getUserRoles: (state) => (Array.isArray(state.user.roles) ? state.user.roles : []),
    getCustomerNavigation: (state) => state.customerNavigation,
    useCustomerNavigation: (state) => state.customerNavigationLoaded,
    hasCustomerNavigationItems: (state) => state.customerNavigation.length > 0,
    getHomeScreenLayout: (state) => state.homeScreenLayout,
    useHomeScreenLayout: (state) => state.homeScreenLayout.length > 0,
    getFeatureDisplayByKey: (state) => state.featureDisplayByKey,
    getFeatureAccessByKey: (state) => state.featureAccessByKey,
    getCustomerLayoutRevision: (state) => state.customerLayoutRevision,
  },
  mutations: {
    updateUserRoles: (state, roles) => {
      state.user.roles = Array.isArray(roles) ? roles : [];
    },
    setToken: (state, data) => {
      localStorage.setItem('jwt', data);
      state.token = data;
      // eslint-disable-next-line no-unused-expressions
      axios.defaults.headers.Authorization = data;
    },
    setRefreshToken: (state, data) => {
      localStorage.setItem('jwt_refresh_token', data);
      state.refresh_token = data;
    },
    setCustomerNavigation: (state, items) => {
      const list = Array.isArray(items) ? items : [];
      state.customerNavigation = list;
      state.customerNavigationLoaded = true;
      state.customerNavigationLoaded = true;
      localStorage.setItem('customerNavigation', JSON.stringify(list));
      localStorage.setItem('customerNavigationLoaded', 'true');
      localStorage.setItem('customerNavigationLoaded', 'true');
    },
    setHomeScreenLayout: (state, sections) => {
      const list = Array.isArray(sections) ? sections : [];
      state.homeScreenLayout = list;
      localStorage.setItem('homeScreenLayout', JSON.stringify(list));
    },
    setFeatureDisplayByKey: (state, byKey) => {
      const map = byKey && typeof byKey === 'object' ? byKey : {};
      state.featureDisplayByKey = map;
      localStorage.setItem('featureDisplayByKey', JSON.stringify(map));
    },
    setFeatureAccessByKey: (state, byKey) => {
      const map = byKey && typeof byKey === 'object' ? byKey : {};
      state.featureAccessByKey = map;
      localStorage.setItem('featureAccessByKey', JSON.stringify(map));
    },
    setCustomerLayoutRevision: (state, revision) => {
      const value = Number(revision) || 0;
      state.customerLayoutRevision = value;
      localStorage.setItem('customerLayoutRevision', String(value));
    },
    setUser: (state, data) => {
      // Ensure preferredLanguage is a valid locale string
      const validLocale = data.preferredLanguage && ['en', 'de'].includes(data.preferredLanguage)
        ? data.preferredLanguage
        : 'en';
      localStorage.setItem('lang', validLocale);
      i18n.global.locale = validLocale;
      state.user = data; // these are the permission flags of the user
    },
    // Keep the cached master flag in sync with the authoritative eligibility
    // result (e.g. after a cycle ends and access is revoked server-side),
    // since `state.user` is only loaded at login.
    setUserExternalEvaluationEnabled: (state, enabled) => {
      if (state.user) {
        state.user.externalEvaluationEnabled = enabled === true;
      }
    },
    setRegisterData: (state, data) => {
      state.registerData = data;
    },
    setRegisterDataValid: (state, data) => {
      state.registerDataValid = data;
    },
    setPasswordResetRequested: (state, data) => {
      state.passwordResetRequested = data;
    },
    setRegistrationSuccessful: (state, data) => {
      state.registrationSuccessful = data;
    },
    setNewPasswordSetSuccesful: (state, data) => {
      state.newPasswordSetSuccesful = data;
    },
    setLanguage: (state, lang) => {
      state.lang = lang;
      state.user.preferredLanguage = lang;
    },
    // Set all tag groups from the new API structure
    setTagGroups: (state, tagGroups) => {
      if (Array.isArray(tagGroups)) {
        state.tagGroups = tagGroups;
      }
    },
    // Toggle selection for unprotected (checkbox) groups
    toggleTagSelection: (state, tagOptionId) => {
      const indexToDelete = state.selectedTags.indexOf(tagOptionId);
      if (indexToDelete > -1) {
        state.selectedTags.splice(indexToDelete, 1);
      } else {
        state.selectedTags.push(tagOptionId);
      }
    },
    // Set answer for protected (radio) groups
    setTagGroupAnswer: (state, { groupId, tagOptionId }) => {
      state.tagGroupAnswers[groupId] = tagOptionId;
    },
    setSelectedTags: (state, data) => {
      state.selectedTags = data;
    },
    removeTagGroupAnswers: (state) => {
      state.tagGroupAnswers = {};
    },
    clearTagGroups: (state) => {
      state.tagGroups = [];
    },
    deleteToken: (state) => {
      localStorage.removeItem('jwt');
      localStorage.removeItem('jwt_refresh_token');
      state.token = null;
      state.refresh_token = null;
      state.customerNavigation = [];
      state.customerNavigationLoaded = false;
      state.homeScreenLayout = [];
      state.featureDisplayByKey = {};
      state.featureAccessByKey = {};
      state.customerLayoutRevision = 0;
      localStorage.removeItem('customerNavigation');
      localStorage.removeItem('customerNavigationLoaded');
      localStorage.removeItem('homeScreenLayout');
      localStorage.removeItem('featureDisplayByKey');
      localStorage.removeItem('featureAccessByKey');
      localStorage.removeItem('customerLayoutRevision');
    },
  },
  actions: {
    async login({
      state,
      commit,
      dispatch,
    }, loginData) {
      const { data } = await NetworkAuth.login(loginData);
      axios.defaults.headers = {
        'Content-Type': 'application/json',
        Authorization: data.token,
      };
      if (state.lang && state.lang !== data.language) {
        dispatch('updateProfile', { preferredLanguage: state.lang });
      }
      commit('setToken', data.token);
      commit('setRefreshToken', data.refresh_token);
      commit('setUser', data.user);
      commit('setCustomerNavigation', data.customerNavigation ?? []);
      commit('setHomeScreenLayout', data.homeScreenLayout ?? []);
      commit('setFeatureDisplayByKey', data.featureDisplayByKey ?? {});
      commit('setFeatureAccessByKey', data.featureAccessByKey ?? {});
    },
    async refreshToken({ state, commit }) {
      const { data } = await NetworkAuth.refreshToken(state.refresh_token);
      commit('setToken', data.token);
      commit('setRefreshToken', data.refresh_token);
    },
    async validateRegister({ commit }, registerData) {
      await NetworkAuth.validateRegisterData(registerData);
      commit('setRegisterData', registerData);
      commit('setRegisterDataValid', true);
    },
    async registerUser({ commit }, registerData) {
      await NetworkAuth.registerUser(registerData);
      commit('setRegistrationSuccessful', true);
      commit('setRegisterData', null);
      commit('setRegisterDataValid', false);
      commit('setSelectedTags', []);
      commit('setSelectedTeams', []);
      commit('setTeams', []);
      commit('clearTagGroups');
      commit('removeTagGroupAnswers');
    },
    // Fetch tag groups (new combined structure)
    async getTagGroups({ commit }) {
      const { data } = await NetworkAuth.getTagGroups();
      commit('setTagGroups', data.data);
    },
    async requestPasswordReset({ commit }, data) {
      await NetworkAuth.requestPasswordReset(data);
      commit('setPasswordResetRequested', true);
    },
    async setNewPassword({ commit }, data) {
      await NetworkAuth.setNewPassword(data);
      commit('setNewPasswordSetSuccesful', true);
    },
    async activateAccount(_, data) {
      await NetworkAuth.activateAccount(data);
    },
    async updateProfile(_, data) {
      // Ensure preferredLanguage is a valid locale string
      const validLocale = data.preferredLanguage && ['en', 'de'].includes(data.preferredLanguage)
        ? data.preferredLanguage
        : 'en';
      localStorage.setItem('lang', validLocale);
      await NetworkAuth.updateProfile(data);
    },
    async uploadProfilePicture({ dispatch, commit, rootState }, base64Image) {
      await FilesService.uploadProfilePicture(base64Image);
      // Refresh user data to get the updated profile picture
      await dispatch('getUserInfo');
      // Update refresh value to force header and other components to refresh
      if (rootState.content) {
        commit('setRefreshValue', null, { root: true });
      }
    },
    async refreshUserRoles() {
      const { refreshSessionFromPing } = await import('@/router/middleware/sessionRefresh');
      await refreshSessionFromPing();
    },
    async getUserInfo({ commit, dispatch, state }) {
      const { data } = await NetworkAuth.getUserInfo();
      commit('setUser', data.user);
      if (state.lang !== data?.user?.preferredLanguage) {
        await dispatch('updateProfile', { preferredLanguage: state.lang });
        // After updating backend, refresh user data to get the updated preferredLanguage
        const { data: updatedData } = await NetworkAuth.getUserInfo();
        commit('setUser', updatedData.user);
      }
      if (data?.user?.preferredLanguage) {
        commit('setLanguage', data.user.preferredLanguage);
      }
    },
    async logout({ commit }) {
      if (import.meta.env.VITE_KEYCLOAK === 'true') {
        commit('deleteToken');
        await keycloak.logout({
          redirectUri: import.meta.env.VITE_KEYCLOAK_LOGOUT_REDIRECT_URI,
        });
      } else {
        commit('deleteToken');
      }
    },
  },
};
